import { rolesApi } from '@/api';
import { queryKeys } from '@/api/query-keys.ts';
import {
  PermissionGroup,
  Role,
  RoleWriteRequest,
} from '@/api/roles/roles.types.ts';
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';

export const useRoles = (options?: UseQueryOptions<Role[], AxiosError>) => {
  return useQuery<Role[], AxiosError>({
    queryKey: queryKeys.roles.all,
    queryFn: () => rolesApi.getRoles().then((res) => res.data),
    ...options,
  });
};

export const usePermissionCatalog = (
  options?: UseQueryOptions<PermissionGroup[], AxiosError>,
) => {
  return useQuery<PermissionGroup[], AxiosError>({
    queryKey: queryKeys.roles.permissionCatalog,
    queryFn: () => rolesApi.getPermissionCatalog().then((res) => res.data),
    staleTime: Infinity,
    ...options,
  });
};

export const useCreateRole = (
  options?: UseMutationOptions<Role, AxiosError, RoleWriteRequest>,
) => {
  const queryClient = useQueryClient();
  return useMutation<Role, AxiosError, RoleWriteRequest>({
    mutationFn: (body) => rolesApi.createRole(body).then((res) => res.data),
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.roles.all });
      options?.onSuccess?.(...args);
    },
  });
};

export const useUpdateRole = (
  options?: UseMutationOptions<
    Role,
    AxiosError,
    { id: string; body: RoleWriteRequest }
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation<Role, AxiosError, { id: string; body: RoleWriteRequest }>({
    mutationFn: ({ id, body }) =>
      rolesApi.updateRole(id, body).then((res) => res.data),
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.roles.all });
      options?.onSuccess?.(...args);
    },
  });
};

export const useDeleteRole = (
  options?: UseMutationOptions<void, AxiosError, string>,
) => {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, string>({
    mutationFn: (id) => rolesApi.deleteRole(id).then((res) => res.data),
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.roles.all });
      options?.onSuccess?.(...args);
    },
  });
};
